import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Camera, Heart, Users, Sparkles } from "lucide-react";
import Button from "../components/Button";

const HomePage = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, -50]);
  const [isMobile, setIsMobile] = useState(false);
  const [showSocialPopup, setShowSocialPopup] = useState(false);

  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  }, []);

  const handleInstagramClick = () => {
    setShowSocialPopup(true);
  };

  const handleContinueToInstagram = () => {
    window.open("https://www.instagram.com/a_la_vueltacabina_360/", "_blank");
    setShowSocialPopup(false);
  };

  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        y,
        backgroundImage: "url(/images/novios.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: isMobile ? "scroll" : "fixed",
      }}
    >
      {/* Overlay con gradiente inspirado en Talavera */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-accent-rose/30 to-secondary/40 backdrop-blur-sm"></div>
      <div className="absolute inset-0 bg-black/15"></div>

      {/* Partículas flotantes con colores Puebla */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${
              i % 3 === 0
                ? "bg-accent-gold/40"
                : i % 2 === 0
                ? "bg-primary-light/30"
                : "bg-white/30"
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Contenido principal */}
      <div className="container mx-auto px-4 py-8 pb-32 relative z-10 flex flex-col items-center justify-center min-h-screen">
        <motion.div
          className="bg-white/95 backdrop-blur-xl border-2 border-accent-gold/30 rounded-card p-6 sm:p-10 shadow-elegant max-w-2xl w-full"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          {/* Foto de la pareja con diseño elegante de boda */}
          <motion.div
            className="flex justify-center mb-8 w-full px-4 overflow-visible"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <div className="relative group w-full max-w-xl overflow-visible">
              {/* Resplandor suave de fondo más amplio */}
              <div className="absolute -inset-12 bg-gradient-to-r from-accent-gold/20 via-primary-light/20 to-accent-gold/20 blur-3xl opacity-40 group-hover:opacity-60 transition-opacity duration-700"></div>

              {/* Contenedor de la imagen con forma de nube más grande */}
              <div className="relative w-full aspect-[16/9] overflow-visible">
                {/* Imagen con bordes difuminados en todos los lados */}
                <div className="relative w-full h-full overflow-visible">
                  <img
                    src="/images/boda.jpg"
                    alt="Nayeli & Enrique"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    style={{
                      objectPosition: "center 25%",
                      maskImage:
                        "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)",
                      maskComposite: "intersect",
                      WebkitMaskImage:
                        "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)",
                      WebkitMaskComposite: "source-in",
                      filter: "drop-shadow(0 10px 30px rgba(0, 0, 0, 0.2))",
                    }}
                  />
                </div>

                {/* Lluvia de corazones animados - más distribuidos */}
                {[...Array(10)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute text-rose-400"
                    style={{
                      left: `${10 + i * 9}%`,
                      top: `-${10 + (i % 3) * 8}%`,
                    }}
                    animate={{
                      y: [0, 350, 350],
                      opacity: [0, 1, 0],
                      scale: [0.3, 1, 0.3],
                      rotate: [0, 360],
                    }}
                    transition={{
                      duration: 4.5 + i * 0.4,
                      repeat: Infinity,
                      delay: i * 0.6,
                      ease: "easeInOut",
                    }}
                  >
                    <Heart
                      className={`${
                        i % 3 === 0
                          ? "w-6 h-6"
                          : i % 3 === 1
                          ? "w-5 h-5"
                          : "w-4 h-4"
                      }`}
                      fill="currentColor"
                      style={{
                        filter: "drop-shadow(0 0 10px rgba(244, 114, 207, 1))",
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Título con Great Vibes */}
          <motion.h1
            className="text-5xl sm:text-7xl font-script text-primary text-center mb-4 leading-tight"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Nayeli & Enrique
          </motion.h1>

          {/* Separador decorativo */}
          <motion.div
            className="flex items-center justify-center gap-3 mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-accent-gold to-transparent"></div>
            <Sparkles className="w-6 h-6 text-accent-gold" />
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-accent-gold to-transparent"></div>
          </motion.div>

          {/* Subtítulo con Playfair Display */}
          <motion.p
            className="font-serif text-primary text-xl sm:text-2xl text-center mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            "Ayúdanos a crear y a revivir este día tan especial"{" "}
          </motion.p>

          {/* Descripción con Montserrat */}
          <motion.p
            className="font-sans text-neutral-warm text-center text-sm sm:text-base leading-relaxed mb-6 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Comparte tus fotos y deja que cada una cuente un pedacito de lo que
            vivimos hoy juntos.
          </motion.p>

          {/* Características con diseño elegante */}
          <motion.div
            className="grid grid-cols-3 gap-3 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <div className="text-center group">
              <div className="p-3 bg-gradient-to-br from-accent-rose to-white rounded-elegant border border-primary/10 mb-2 mx-auto w-fit transition-all duration-300 group-hover:shadow-soft group-hover:scale-105">
                <Camera className="w-6 h-6 text-primary" />
              </div>
              <p className="font-sans text-neutral-warm text-xs font-medium">
                Toma fotos
              </p>
            </div>
            <div className="text-center group">
              <div className="p-3 bg-gradient-to-br from-accent-rose to-white rounded-elegant border border-secondary/10 mb-2 mx-auto w-fit transition-all duration-300 group-hover:shadow-soft group-hover:scale-105">
                <Heart className="w-6 h-6 text-secondary" />
              </div>
              <p className="font-sans text-neutral-warm text-xs font-medium">
                Da me gusta
              </p>
            </div>
            <div className="text-center group">
              <div className="p-3 bg-gradient-to-br from-accent-rose to-white rounded-elegant border border-accent-gold/30 mb-2 mx-auto w-fit transition-all duration-300 group-hover:shadow-soft group-hover:scale-105">
                <Users className="w-6 h-6 text-accent-jade" />
              </div>
              <p className="font-sans text-neutral-warm text-xs font-medium">
                Comparte
              </p>
            </div>
          </motion.div>

          {/* Botón principal con nuevo diseño */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, type: "spring", stiffness: 200 }}
          >
            <Link to="/event-naye&kike" className="block">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                icon={<Camera className="w-5 h-5" />}
              >
                <span className="text-xl">Comenzar a compartir</span>
              </Button>
            </Link>
          </motion.div>

          {/* Botón secundario - Ver galería */}
          <motion.div
            className="mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <Link to="/event-naye&kike" className="block">
              <Button
                variant="outline"
                size="md"
                fullWidth
                icon={<Heart className="w-5 h-5" />}
              >
                Ver galería de recuerdos
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Footer con estilo Puebla */}
      <footer className="absolute bottom-0 left-0 right-0 py-6 bg-gradient-to-t from-primary/90 to-transparent backdrop-blur-md z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-12 h-px bg-accent-gold/50"></div>
          <Heart className="w-4 h-4 text-accent-gold" fill="currentColor" />
          <div className="w-12 h-px bg-accent-gold/50"></div>
        </div>
        <p className="font-sans text-white/90 text-center text-sm">
          <span className="font-semibold">photobook.alavuelta360.com.mx</span>
        </p>
        <p
          className="font-sans text-white/70 text-center text-xs mt-1 cursor-pointer hover:text-accent-gold transition-colors duration-300"
          onClick={handleInstagramClick}
        >
          @a_la_vueltacabina_360
        </p>
      </footer>

      {/* Popup de invitación a redes sociales */}
      {showSocialPopup && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowSocialPopup(false)}
        >
          <motion.div
            className="bg-gradient-to-br from-primary via-secondary to-primary-dark rounded-2xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden"
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decoración superior */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent-gold via-accent-rose to-accent-gold"></div>

            {/* Patrón decorativo */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent-gold/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent-rose/10 rounded-full translate-y-12 -translate-x-12"></div>

            <div className="relative z-10">
              {/* Icono */}
              <div className="flex justify-center mb-6">
                <div className="bg-accent-gold/20 p-4 rounded-full">
                  <Heart
                    className="w-10 h-10 text-accent-gold"
                    fill="currentColor"
                  />
                </div>
              </div>

              {/* Título */}
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-white text-center mb-4">
                ¡Síguenos en Instagram!
              </h3>

              {/* Mensaje */}
              <p className="font-sans text-white/90 text-center mb-6 leading-relaxed">
                Nos encantaría que formes parte de nuestra comunidad. Descubre
                más momentos especiales, promociones exclusivas y contenido
                detrás de cámaras.
              </p>

              {/* Detalles */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                <div className="flex items-center justify-center gap-3">
                  <Sparkles className="w-5 h-5 text-accent-gold" />
                  <p className="font-sans text-white font-semibold text-lg">
                    @a_la_vueltacabina_360
                  </p>
                  <Sparkles className="w-5 h-5 text-accent-gold" />
                </div>
              </div>

              {/* Botones */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleContinueToInstagram}
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-pink-500/50 transform hover:scale-105"
                >
                  Ir a Instagram
                </button>
                <button
                  onClick={() => setShowSocialPopup(false)}
                  className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 border border-white/30 hover:border-white/50"
                >
                  Tal vez después
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default HomePage;
